/**
 * Cross-mount dedupe for booking conversion events (AHD-9067).
 *
 * `ConfirmationStep` fires exactly one conversion event per booking —
 * `booking_complete`, or `membership_redemption` (+ `Booking_complete_membership_addon`)
 * for an entitled member. Its only guard used to be a `useRef`, which dies with
 * the component instance, so any remount re-fired the event:
 *
 *  - the user reopens the booking app in a new tab or from a bookmark and the
 *    persisted session (`sessionId` cookie -> `loadSession`) restores the cart;
 *  - `membershipToken` is absent from that URL, so `MembershipContext` never
 *    fetches and `isMember` stays false — a member's redemption is then
 *    re-reported as a plain paid `booking_complete`, inflating the PostHog
 *    "Hard conversion" action.
 *
 * Booking ids are stable and outlive the page (see `getOrCreateBookingId` in
 * bookingFlowStore — it is persisted with the session snapshot so it survives a
 * TWINT redirect reload), which makes them the right dedupe key.
 *
 * Ids are stored as a comma-separated list rather than JSON: booking ids are
 * UUIDs, so no escaping is needed and there is no parse failure mode to handle.
 */

import { safeLocalStorage } from "./safeStorage";

const STORAGE_KEY = "ahead:tracked-conversions";

/**
 * How many booking ids to remember. A booking id is minted per booking, so this
 * only needs to outlive the window in which a stale confirmation page can be
 * remounted. Bounded so the entry can't grow without limit on a shared device
 * (clinic tablets book for many patients in a row).
 */
const MAX_REMEMBERED = 20;

const readIds = (): string[] => {
  const raw = safeLocalStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  return raw.split(",").filter((id) => id.length > 0);
};

/**
 * True when a conversion event has already been reported for this booking.
 *
 * Note this deliberately survives `clearCart()` and the confirmation page's
 * localStorage cleanup — those clear the *booking*, this remembers that the
 * booking was already counted.
 */
export const hasTrackedConversion = (bookingId: string): boolean =>
  readIds().includes(bookingId);

/** Record that a conversion event has been reported for this booking. */
export const markConversionTracked = (bookingId: string): void => {
  const existing = readIds();
  if (existing.includes(bookingId)) return;
  const next = [...existing, bookingId].slice(-MAX_REMEMBERED);
  safeLocalStorage.setItem(STORAGE_KEY, next.join(","));
};
